import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { userService } from '../../../services/userService';

const ProfileTab = () => {
  const { t } = useTranslation();
  const { user, userProfile } = useAuth();
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: '',
    job_title: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (userProfile) {
      setProfileData({
        full_name: userProfile?.full_name || '',
        email: userProfile?.email || '',
        phone: userProfile?.phone || '',
        job_title: userProfile?.job_title || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        initials: userProfile?.full_name
          ? userProfile?.full_name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()
          : user?.email?.[0]?.toUpperCase()
      });
    }
  }, [userProfile]);

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!profileData?.full_name?.trim()) {
      newErrors.full_name = t('accountSettings.profile.errors.fullNameRequired');
    }

    if (!profileData?.email?.trim()) {
      newErrors.email = t('accountSettings.profile.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(profileData?.email)) {
      newErrors.email = t('accountSettings.profile.errors.emailInvalid');
    }

    if (profileData?.newPassword) {
      if (!profileData?.currentPassword) {
        newErrors.currentPassword = t('accountSettings.profile.errors.currentPasswordRequired');
      }
      if (profileData?.newPassword?.length < 8) {
        newErrors.newPassword = t('accountSettings.profile.errors.newPasswordMinLength');
      }
      if (profileData?.newPassword !== profileData?.confirmPassword) {
        newErrors.confirmPassword = t('accountSettings.profile.errors.passwordMismatch');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (validateForm()) {
      setIsSaving(true);
      try {
        await userService?.updateProfile({
          full_name: profileData?.full_name,
          phone: profileData?.phone,
          job_title: profileData?.job_title
        });

        if (profileData?.newPassword) {
          await userService?.updatePassword(profileData?.newPassword);
        }

        setIsEditing(false);
        setProfileData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      } catch (error) {
        setErrors({ submit: error?.message });
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setErrors({});
    setProfileData(prev => ({
      ...prev,
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }));
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">{t('accountSettings.profile.title')}</h2>
          <p className="text-sm md:text-base text-muted-foreground mt-1">
            {t('accountSettings.profile.subtitle')}
          </p>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
            className="w-full sm:w-auto"
          >
            {t('accountSettings.profile.editProfile')}
          </Button>
        )}
      </div>
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
        <div className="flex flex-col items-center mb-6 md:mb-8">
          <div className="w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-full bg-accent/20 flex items-center justify-center text-accent text-2xl md:text-3xl lg:text-4xl font-semibold mb-3 md:mb-4">
            {profileData?.initials}
          </div>
          {isEditing && (
            <Button variant="ghost" size="sm" iconName="Camera" iconPosition="left">
              {t('accountSettings.profile.changePhoto')}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <Input
            label={t('accountSettings.profile.fullName')}
            type="text"
            value={profileData?.full_name}
            onChange={(e) => handleInputChange('full_name', e?.target?.value)}
            error={errors?.full_name}
            disabled={!isEditing}
            required
          />

          <Input
            label={t('accountSettings.profile.emailAddress')}
            type="email"
            value={profileData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            disabled={!isEditing}
            required
          />

          <Input
            label={t('accountSettings.profile.phone')}
            type="tel"
            value={profileData?.phone}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            disabled={!isEditing}
          />

          <Input
            label={t('accountSettings.profile.jobTitle')}
            type="text"
            value={profileData?.job_title}
            onChange={(e) => handleInputChange('job_title', e?.target?.value)}
            disabled={!isEditing}
          />
        </div>

        {isEditing && (
          <>
            <div className="border-t border-border my-6 md:my-8" />
            
            <div className="space-y-4 md:space-y-6">
              <h3 className="text-base md:text-lg font-semibold text-foreground">{t('accountSettings.profile.changePassword')}</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                <Input
                  label={t('accountSettings.profile.currentPassword')}
                  type="password"
                  value={profileData?.currentPassword}
                  onChange={(e) => handleInputChange('currentPassword', e?.target?.value)}
                  error={errors?.currentPassword}
                  placeholder={t('accountSettings.profile.currentPasswordPlaceholder')}
                />

                <div className="lg:col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                  <Input
                    label={t('accountSettings.profile.newPassword')}
                    type="password"
                    value={profileData?.newPassword}
                    onChange={(e) => handleInputChange('newPassword', e?.target?.value)}
                    error={errors?.newPassword}
                    placeholder={t('accountSettings.profile.newPasswordPlaceholder')}
                    description={t('accountSettings.profile.newPasswordDescription')}
                  />

                  <Input
                    label={t('accountSettings.profile.confirmPassword')}
                    type="password"
                    value={profileData?.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                    error={errors?.confirmPassword}
                    placeholder={t('accountSettings.profile.confirmPasswordPlaceholder')}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {isEditing && (
          <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
            <Button
              variant="default"
              onClick={handleSave}
              loading={isSaving}
              iconName="Check"
              iconPosition="left"
              className="w-full sm:w-auto"
            >
              {t('accountSettings.profile.saveChanges')}
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              className="w-full sm:w-auto"
            >
              {t('accountSettings.profile.cancel')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileTab;